import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Warning, ListNumbers, Lightning, Check } from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Best-Thirds Tiebreak Card — shown on a tournament's Bracket tab when
 * `qualifier_pool.tie_info` is present (auto-picker found ties at the
 * cut-off). Offers the admin two paths:
 *
 *   1. "Pick Manually" — drag-to-reorder dialog (in priority order)
 *   2. "Blue Ball Shootout" — creates best-of-5 round-robin among tied
 *      players; once those matches complete, admin finalises and the
 *      shootout winners become the override.
 */
export default function BestThirdsTiebreakCard({ tournamentId, tieInfo, onResolved }) {
  const [busy, setBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [order, setOrder] = useState(tieInfo?.tied_players?.map((p) => p.player_id) || []);

  if (!tieInfo) return null;
  const { tied_players, slots_needed, tied_stats, resolved_by } = tieInfo;

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...order];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setOrder(next);
  };
  const moveDown = (idx) => {
    if (idx === order.length - 1) return;
    const next = [...order];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    setOrder(next);
  };

  const saveManual = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/tournaments/${tournamentId}/tiebreak/manual`,
        { winners: order },
        { withCredentials: true },
      );
      toast.success("Tie resolved manually");
      setManualOpen(false);
      onResolved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const createShootout = async () => {
    if (!window.confirm(
      `Create a Blue Ball Shootout among ${tied_players.length} tied players?\n\n` +
      `Best-of-5 round-robin: ${tied_players.length * (tied_players.length - 1) / 2} match(es).\n\n` +
      `Once all shootout matches are completed, come back to this card and click "Finalize Shootout" to apply the result.`,
    )) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/tournaments/${tournamentId}/tiebreak/shootout`,
        {
          player_ids: tied_players.map((p) => p.player_id),
          slots_needed,
        },
        { withCredentials: true },
      );
      toast.success(r.data.message);
      onResolved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create shootout");
    } finally { setBusy(false); }
  };

  const finalizeShootout = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/tournaments/${tournamentId}/tiebreak/finalize`,
        {},
        { withCredentials: true },
      );
      toast.success(r.data.message);
      onResolved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to finalise");
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-amber-500/40 bg-amber-500/5" data-testid="tiebreak-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-2">
          <Warning className="w-5 h-5 text-amber-400 mt-0.5" weight="fill" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-amber-300">
              Tie detected among best-{tieInfo.tied_players[0]?.group_name ? tieInfo.tied_players[0] && (tied_players[0].group_name ? "" : "") : ""}Nth-place finishers
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {tied_players.length} players tied on {tied_stats.points}pts ·{" "}
              {tied_stats.frame_diff >= 0 ? "+" : ""}{tied_stats.frame_diff} frame diff ·{" "}
              {tied_stats.frames_won} frames won. <strong className="text-foreground">{slots_needed}</strong>{" "}
              of them must be picked.
            </p>
            {resolved_by === "override" && (
              <Badge variant="outline" className="mt-2 text-[10px] border-emerald-500/40 text-emerald-400">
                <Check className="w-3 h-3 mr-1" weight="bold" />
                Override applied
              </Badge>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="tied-players-preview">
          {tied_players.map((p) => (
            <Badge key={p.player_id} variant="outline" className="text-[11px] border-amber-500/30 text-amber-300">
              {p.group_name}: {p.name}
            </Badge>
          ))}
        </div>

        {resolved_by !== "override" && (
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              size="sm" variant="outline"
              onClick={() => setManualOpen(true)}
              disabled={busy}
              data-testid="tiebreak-manual-btn"
            >
              <ListNumbers className="w-4 h-4 mr-1.5" />
              Pick Manually
            </Button>
            <Button
              size="sm"
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
              onClick={createShootout}
              disabled={busy}
              data-testid="tiebreak-shootout-btn"
            >
              <Lightning className="w-4 h-4 mr-1.5" weight="fill" />
              Blue Ball Shootout (best-of-5)
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={finalizeShootout}
              disabled={busy}
              data-testid="tiebreak-finalize-btn"
            >
              <Check className="w-4 h-4 mr-1.5" weight="bold" />
              Finalize Shootout
            </Button>
          </div>
        )}
      </CardContent>

      {/* Manual pick dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="tiebreak-manual-dialog">
          <DialogHeader>
            <DialogTitle>Pick qualifier priority</DialogTitle>
            <DialogDescription className="text-xs">
              Highest priority at top. The first <strong>{slots_needed}</strong> will get bracket slots.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 my-2">
            {order.map((pid, idx) => {
              const player = tied_players.find((p) => p.player_id === pid);
              if (!player) return null;
              return (
                <div
                  key={pid}
                  className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md border ${
                    idx < slots_needed
                      ? "border-emerald-500/40 bg-emerald-500/5"
                      : "border-border bg-background/30"
                  }`}
                  data-testid={`tiebreak-rank-row-${pid}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-semibold tabular-nums text-muted-foreground w-5">
                      {idx + 1}.
                    </span>
                    <span className="text-sm truncate">
                      {player.name}
                      <span className="text-muted-foreground ml-1 text-[10px]">
                        (Grp {player.group_name})
                      </span>
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-6 w-6"
                      onClick={() => moveUp(idx)} disabled={idx === 0}
                      data-testid={`tiebreak-up-${pid}`}>↑</Button>
                    <Button size="icon" variant="ghost" className="h-6 w-6"
                      onClick={() => moveDown(idx)} disabled={idx === order.length - 1}
                      data-testid={`tiebreak-down-${pid}`}>↓</Button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setManualOpen(false)}>Cancel</Button>
            <Button
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={saveManual} disabled={busy}
              data-testid="tiebreak-save-btn"
            >Save Priority</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
